import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import Confetti from 'react-confetti';
import { toast } from 'sonner';
import {
  Rocket, Flag, Blocks, GraduationCap, Trophy, Lock,
  CheckCircle, ChevronRight, Sparkles, BookOpen, Compass,
  Award, FlaskConical, Cpu, Cog, Calculator, ArrowRight,
  Star, Zap, Target, ShieldCheck, PartyPopper
} from 'lucide-react';

// Icon mapping for milestones
const ICON_MAP = {
  Rocket: Rocket, Flag: Flag, Blocks: Blocks,
  GraduationCap: GraduationCap, Trophy: Trophy,
  BookOpen: BookOpen, Compass: Compass, Award: Award,
};

// Category icons
const CATEGORY_ICONS = {
  Science: FlaskConical, Technology: Cpu,
  Engineering: Cog, Math: Calculator,
};
const CATEGORY_COLORS = {
  Science: { bg: 'bg-green-500/20', text: 'text-green-400', bar: 'bg-green-500' },
  Technology: { bg: 'bg-blue-500/20', text: 'text-blue-400', bar: 'bg-blue-500' },
  Engineering: { bg: 'bg-orange-500/20', text: 'text-orange-400', bar: 'bg-orange-500' },
  Math: { bg: 'bg-purple-500/20', text: 'text-purple-400', bar: 'bg-purple-500' },
};

export default function GamificationDashboard() {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [learners, setLearners] = useState([]);
  const [selectedLearnerId, setSelectedLearnerId] = useState(null);
  const [scoreData, setScoreData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [animatedScore, setAnimatedScore] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [newlyUnlocked, setNewlyUnlocked] = useState([]);

  // Fetch learners
  useEffect(() => {
    const fetchLearners = async () => {
      try {
        const res = await api.get('/learners/');
        const list = res.data.results || res.data;
        setLearners(list);
        if (list.length > 0) setSelectedLearnerId(list[0].id);
      } catch (e) {
        console.error('Error fetching learners:', e);
      }
    };
    fetchLearners();
  }, []);

  // Fetch STEM score for selected learner
  const fetchScore = useCallback(async () => {
    if (!selectedLearnerId) return;
    setLoading(true);
    try {
      const res = await api.get(`/gamification/stem-score/${selectedLearnerId}/`);
      setScoreData(res.data);
      // Check for newly unlocked milestones
      if (res.data.newly_awarded && res.data.newly_awarded.length > 0) {
        setNewlyUnlocked(res.data.newly_awarded);
        setShowConfetti(true);
        res.data.newly_awarded.forEach(ms => {
          toast.success(`Milestone Unlocked: ${ms.milestone_name}!`, {
            description: `+${ms.points_awarded} bonus points & ${ms.badge_name}`,
            duration: 6000,
          });
        });
        setTimeout(() => setShowConfetti(false), 8000);
      }
    } catch (e) {
      console.error('Error fetching STEM score:', e);
    } finally {
      setLoading(false);
    }
  }, [selectedLearnerId]);

  useEffect(() => { fetchScore(); }, [fetchScore]);

  // Animate score counter
  useEffect(() => {
    if (!scoreData) return;
    const target = scoreData.score;
    const duration = 1200;
    const start = performance.now();
    const animate = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setAnimatedScore(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(animate);
    };
    setAnimatedScore(0);
    requestAnimationFrame(animate);
  }, [scoreData]);

  if (loading && !scoreData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!scoreData) return null;

  const { score, milestones, next_milestone, stats, category_progress, recommended_actions, learner } = scoreData;

  return (
    <div className="space-y-6" data-testid="gamification-dashboard">
      {/* Confetti Animation */}
      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={400}
          gravity={0.15}
          style={{ position: 'fixed', top: 0, left: 0, zIndex: 9999 }}
          data-testid="confetti-animation"
        />
      )}

      {/* Newly Unlocked Milestone Banner */}
      {newlyUnlocked.length > 0 && (
        <div className="rounded-xl bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 border-2 border-amber-200 p-4" data-testid="milestone-unlock-banner">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center animate-bounce">
              <Trophy className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-bold text-amber-900">
                {newlyUnlocked.length === 1
                  ? `${newlyUnlocked[0].milestone_name} Unlocked!`
                  : `${newlyUnlocked.length} Milestones Unlocked!`}
              </h3>
              <p className="text-sm text-amber-700">
                {newlyUnlocked.map(m => `+${m.points_awarded} pts`).join(' | ')}
                {' '}&mdash; Check your <Link to="/parent/certificates" className="underline font-medium">certificates</Link>!
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Learner Selector (if multiple) */}
      {learners.length > 1 && (
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-muted-foreground">Viewing progress for:</span>
          <div className="flex gap-2">
            {learners.map(l => (
              <button
                key={l.id}
                onClick={() => setSelectedLearnerId(l.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedLearnerId === l.id
                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/25'
                    : 'bg-white border border-gray-200 text-gray-600 hover:border-indigo-300'
                }`}
              >
                {l.first_name} {l.last_name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ─── Main Score Hero ─── */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1a1147] via-[#2d1b69] to-[#1e3a5f] p-8 text-white">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-indigo-500/10 to-transparent rounded-full -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-purple-500/10 to-transparent rounded-full translate-y-1/2 -translate-x-1/4" />
        <div className="absolute top-4 right-8 text-indigo-400/20"><Sparkles className="h-12 w-12" /></div>

        <div className="relative z-10">
          {/* Score header */}
          <div className="flex items-start justify-between mb-2">
            <div>
              <h2 className="text-lg font-medium text-indigo-200 tracking-wide uppercase">
                STEM Readiness Score
              </h2>
              <p className="text-white/60 text-sm mt-1">
                {learner?.name}'s learning journey — keep going to unlock rewards!
              </p>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-3 py-1.5">
              <Star className="h-4 w-4 text-yellow-400" />
              <span className="text-sm font-medium">{stats.points_balance} pts</span>
            </div>
          </div>

          {/* Big score */}
          <div className="flex items-end gap-2 mt-6 mb-8">
            <span className="text-7xl font-bold tabular-nums leading-none tracking-tight">
              {animatedScore}
            </span>
            <span className="text-3xl font-light text-white/60 mb-2">%</span>
          </div>

          {/* ─── Progress Bar with Milestones ─── */}
          <div className="relative mt-4 mb-2">
            {/* Track */}
            <div className="h-3 bg-white/10 rounded-full overflow-hidden backdrop-blur-sm">
              <div
                className="h-full rounded-full transition-all duration-1000 ease-out"
                style={{
                  width: `${Math.max(score, 2)}%`,
                  background: 'linear-gradient(90deg, #818cf8, #6366f1, #a78bfa)',
                }}
              />
            </div>

            {/* Milestone markers */}
            <div className="relative h-16 mt-1">
              {milestones.map((ms) => {
                const Icon = ICON_MAP[ms.icon] || Star;
                return (
                  <div
                    key={ms.id}
                    className="absolute flex flex-col items-center -translate-x-1/2"
                    style={{ left: `${ms.threshold}%` }}
                  >
                    {/* Tick */}
                    <div className="w-0.5 h-2 bg-white/30" />
                    {/* Icon circle */}
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all ${
                      ms.unlocked
                        ? 'bg-indigo-500 border-indigo-300 shadow-lg shadow-indigo-500/40'
                        : ms.current
                          ? 'bg-white/10 border-indigo-400 shadow-lg shadow-indigo-500/20 animate-pulse'
                          : 'bg-white/5 border-white/20'
                    }`}>
                      {ms.unlocked ? (
                        <CheckCircle className="h-4 w-4 text-white" />
                      ) : (
                        <Icon className={`h-4 w-4 ${ms.current ? 'text-indigo-300' : 'text-white/30'}`} />
                      )}
                    </div>
                    {/* Label */}
                    <span className={`text-[10px] mt-0.5 whitespace-nowrap font-medium ${
                      ms.unlocked ? 'text-indigo-300' : ms.current ? 'text-white/80' : 'text-white/30'
                    }`}>
                      {ms.threshold}%
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Milestone legend (horizontal) */}
          <div className="flex flex-wrap gap-4 mt-2">
            {milestones.map((ms) => {
              const Icon = ICON_MAP[ms.icon] || Star;
              return (
                <div key={ms.id} className={`flex items-center gap-1.5 text-xs ${
                  ms.unlocked ? 'text-indigo-200' : ms.current ? 'text-white/70' : 'text-white/30'
                }`}>
                  <Icon className="h-3 w-3" />
                  <span>{ms.name}</span>
                  {ms.unlocked && <CheckCircle className="h-3 w-3 text-green-400" />}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ─── Next Milestone + CTA Cards ─── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Next Milestone Card */}
        {next_milestone && (
          <Card className="border-2 border-indigo-100 bg-gradient-to-br from-indigo-50 to-white overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  {(() => {
                    const Icon = ICON_MAP[next_milestone.icon] || Target;
                    return <Icon className="h-6 w-6 text-indigo-600" />;
                  })()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-gray-900">Next: {next_milestone.name}</h3>
                    <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs">
                      {next_milestone.percent_to_go}% to go
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{next_milestone.description}</p>
                  {/* Mini progress to next milestone */}
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Progress</span>
                      <span>{score}% / {next_milestone.threshold}%</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-700"
                        style={{ width: `${(score / next_milestone.threshold) * 100}%` }}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 mt-3 text-xs text-indigo-600">
                    <Zap className="h-3 w-3" />
                    <span className="font-medium">Reward: {next_milestone.reward}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* CTA Card */}
        <Card className="border-2 border-emerald-100 bg-gradient-to-br from-emerald-50 to-white overflow-hidden">
          <CardContent className="p-6 flex flex-col justify-between h-full">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-5 w-5 text-emerald-600" />
                <h3 className="font-bold text-gray-900">Keep Building Your Score!</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Enroll in courses across all STEM categories to unlock milestones, earn badges, and redeem exclusive rewards.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => navigate('/catalog')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1 group"
                data-testid="browse-courses-btn"
              >
                Browse Courses
                <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => navigate('/parent/certificates')}
                variant="outline"
                className="border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                data-testid="view-certificates-btn"
              >
                <ShieldCheck className="h-4 w-4 mr-1" /> Certs
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ─── Category Progress Grid ─── */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Compass className="h-5 w-5 text-indigo-600" />
          Category Progress
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(category_progress).map(([cat, data]) => {
            const Icon = CATEGORY_ICONS[cat] || Star;
            const colors = CATEGORY_COLORS[cat] || CATEGORY_COLORS.Science;
            return (
              <Card key={cat} className={`relative overflow-hidden transition-all hover:shadow-md ${
                data.has_activity ? 'border-2' : 'border opacity-80'
              }`} style={data.has_activity ? { borderColor: `var(--${cat.toLowerCase()}-border, #c7d2fe)` } : {}}>
                <CardContent className="p-4">
                  <div className={`w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center mb-3`}>
                    <Icon className={`h-5 w-5 ${colors.text}`} />
                  </div>
                  <h4 className="font-semibold text-gray-900 text-sm">{cat}</h4>
                  <div className="mt-2 space-y-1">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{data.enrolled} enrolled</span>
                      <span>{data.completed} done</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${colors.bar}`}
                        style={{ width: data.enrolled > 0 ? `${Math.max(20, (data.completed / Math.max(data.enrolled, 1)) * 100)}%` : '0%' }}
                      />
                    </div>
                  </div>
                  {!data.has_activity && (
                    <Link to="/catalog" className="flex items-center gap-1 text-xs text-indigo-600 mt-2 hover:underline">
                      Start exploring <ChevronRight className="h-3 w-3" />
                    </Link>
                  )}
                  {data.has_activity && (
                    <div className="flex items-center gap-1 text-xs text-green-600 mt-2">
                      <CheckCircle className="h-3 w-3" /> Active
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* ─── Recommended Actions ─── */}
      {recommended_actions && recommended_actions.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Target className="h-5 w-5 text-indigo-600" />
            Recommended Next Steps
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {recommended_actions.map((action, i) => {
              const Icon = ICON_MAP[action.icon] || CATEGORY_ICONS[action.icon] || ChevronRight;
              return (
                <Card key={i} className="group hover:shadow-md transition-all cursor-pointer border hover:border-indigo-200"
                      onClick={() => navigate(action.link)}>
                  <CardContent className="p-4">
                    <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center mb-2 group-hover:bg-indigo-100 transition-colors">
                      <Icon className="h-4 w-4 text-indigo-600" />
                    </div>
                    <h4 className="font-medium text-gray-900 text-sm mb-1">{action.title}</h4>
                    <p className="text-xs text-gray-500 line-clamp-2">{action.description}</p>
                    <div className="flex items-center gap-1 text-xs text-indigo-600 mt-2 font-medium group-hover:underline">
                      Go <ChevronRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── Milestone Roadmap ─── */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Rocket className="h-5 w-5 text-indigo-600" />
          Milestone Roadmap
        </h3>
        <div className="space-y-3">
          {milestones.map((ms, i) => {
            const Icon = ICON_MAP[ms.icon] || Star;
            return (
              <div
                key={ms.id}
                className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${
                  ms.unlocked
                    ? 'bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200'
                    : ms.current
                      ? 'bg-white border-indigo-300 shadow-sm ring-1 ring-indigo-100'
                      : 'bg-gray-50 border-gray-100'
                }`}
              >
                {/* Number / check */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm ${
                  ms.unlocked
                    ? 'bg-indigo-600 text-white'
                    : ms.current
                      ? 'bg-white border-2 border-indigo-400 text-indigo-600'
                      : 'bg-gray-200 text-gray-400'
                }`}>
                  {ms.unlocked ? <CheckCircle className="h-5 w-5" /> : i + 1}
                </div>

                {/* Icon */}
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  ms.unlocked ? 'bg-indigo-100' : ms.current ? 'bg-indigo-50' : 'bg-gray-100'
                }`}>
                  <Icon className={`h-5 w-5 ${
                    ms.unlocked ? 'text-indigo-600' : ms.current ? 'text-indigo-400' : 'text-gray-300'
                  }`} />
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className={`font-semibold text-sm ${
                      ms.unlocked ? 'text-indigo-900' : ms.current ? 'text-gray-900' : 'text-gray-400'
                    }`}>
                      {ms.name}
                    </h4>
                    {ms.unlocked && (
                      <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px]">Unlocked!</Badge>
                    )}
                    {ms.current && (
                      <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 text-[10px] animate-pulse">Next</Badge>
                    )}
                  </div>
                  <p className={`text-xs mt-0.5 ${
                    ms.unlocked ? 'text-indigo-700/70' : ms.current ? 'text-gray-500' : 'text-gray-300'
                  }`}>
                    {ms.description}
                  </p>
                </div>

                {/* Threshold */}
                <div className={`text-right flex-shrink-0 ${
                  ms.unlocked ? 'text-indigo-600' : ms.current ? 'text-gray-700' : 'text-gray-300'
                }`}>
                  <div className="text-lg font-bold">{ms.threshold}%</div>
                  <div className="text-[10px]">{ms.reward.split('+')[0].trim()}</div>
                </div>

                {/* Lock icon for locked */}
                {!ms.unlocked && !ms.current && (
                  <Lock className="h-4 w-4 text-gray-300 flex-shrink-0" />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
